import { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { getPowerTranzPaymentStatus } from "store/payment-provider"
import { mapPowerTranzPaymentStatus } from "store/powertranzApi"
import { isPendingAttemptState } from "helpers/powertranz"
import {
  STATUS_POLL_FAST_DURATION_MS,
  STATUS_POLL_FAST_INTERVAL_MS,
  STATUS_POLL_MAX_DURATION_MS,
  STATUS_POLL_SLOW_INTERVAL_MS
} from "constants/powertranz"

const NOT_FOUND_STATUSES = [401, 403, 404]

// Asks the API for the attempt's real outcome — the only source of truth; the URL the browser
// landed on is just a hint. Polls while the outcome is pending: every 3 s for the first minute,
// then every 15 s, and stops at a terminal state. Pass null to skip polling.
export const usePowerTranzStatusPolling = (params) => {
  const dispatch = useDispatch()
  const [status, setStatus] = useState(null)
  const [isRequestFailed, setIsRequestFailed] = useState(false)
  // The API answered that this payment can't be looked up (not found, or not this user's booking):
  // retrying won't change that, so polling stops.
  const [isNotFound, setIsNotFound] = useState(false)
  // Still unconfirmed after 10 minutes: a backend job settles it and emails the guest, so stop.
  const [isTimedOut, setIsTimedOut] = useState(false)
  const startedAtRef = useRef(Date.now())
  const paramsKey = JSON.stringify(params)

  useEffect(() => {
    if (paramsKey === "null") return undefined
    let isActive = true
    let timer = null
    startedAtRef.current = Date.now()
    // A new attempt (retry, start again) starts from scratch, not from the last one's outcome.
    setStatus(null)
    setIsRequestFailed(false)
    setIsNotFound(false)
    setIsTimedOut(false)

    const poll = async () => {
      let nextStatus = null
      try {
        nextStatus = mapPowerTranzPaymentStatus(await dispatch(getPowerTranzPaymentStatus(JSON.parse(paramsKey))))
        if (!isActive) return
        setStatus(nextStatus)
        setIsRequestFailed(false)
      } catch (error) {
        if (!isActive) return
        if (NOT_FOUND_STATUSES.includes(error?.status)) {
          setIsNotFound(true)
          return
        }
        // Keep polling: a dropped request says nothing about the payment itself.
        setIsRequestFailed(true)
      }
      if (nextStatus && !isPendingAttemptState(nextStatus.state)) return
      const elapsed = Date.now() - startedAtRef.current
      if (elapsed >= STATUS_POLL_MAX_DURATION_MS) {
        setIsTimedOut(true)
        return
      }
      timer = setTimeout(poll, elapsed < STATUS_POLL_FAST_DURATION_MS ? STATUS_POLL_FAST_INTERVAL_MS : STATUS_POLL_SLOW_INTERVAL_MS)
    }

    poll()
    return () => {
      isActive = false
      clearTimeout(timer)
    }
  }, [dispatch, paramsKey])

  return { status, isRequestFailed, isNotFound, isTimedOut }
}
